import Phaser from 'phaser';
import { INTERNAL_HEIGHT, INTERNAL_WIDTH } from '@/game/config/GameConfig';
import { PALETTE, createButton, titleTextStyle } from '@/game/ui/theme';
import type { GameplayScene } from './GameplayScene';

export interface PauseSceneData {
  gameplaySceneKey: string;
}

/** A lightweight overlay scene launched on top of a paused GameplayScene. Keeping it
 * as its own scene (rather than an in-scene modal) means the pause menu keeps
 * receiving input/tweens even while gameplay's update loop is fully stopped. */
export class PauseScene extends Phaser.Scene {
  private gameplaySceneKey!: string;

  constructor() {
    super('PauseScene');
  }

  init(data: PauseSceneData): void {
    this.gameplaySceneKey = data.gameplaySceneKey;
  }

  create(): void {
    const overlay = this.add.rectangle(0, 0, INTERNAL_WIDTH, INTERNAL_HEIGHT, 0x150e26, 0.72).setOrigin(0);

    const panel = this.add.graphics();
    const panelW = 460;
    const panelH = 420;
    const panelX = INTERNAL_WIDTH / 2 - panelW / 2;
    const panelY = INTERNAL_HEIGHT / 2 - panelH / 2;
    panel.fillStyle(PALETTE.panel, 1);
    panel.fillRoundedRect(panelX, panelY, panelW, panelH, 28);
    panel.lineStyle(4, PALETTE.panelLight, 1);
    panel.strokeRoundedRect(panelX, panelY, panelW, panelH, 28);

    this.add.text(INTERNAL_WIDTH / 2, panelY + 56, 'Paused', titleTextStyle(44)).setOrigin(0.5);

    const resumeBtn = createButton(this, INTERNAL_WIDTH / 2, panelY + 160, 320, 76, 'Resume', PALETTE.success);
    const restartBtn = createButton(this, INTERNAL_WIDTH / 2, panelY + 250, 320, 76, 'Restart', PALETTE.secondary);
    const quitBtn = createButton(this, INTERNAL_WIDTH / 2, panelY + 340, 320, 76, 'Quit', PALETTE.danger);

    resumeBtn.on('pointerup', () => this.resume());
    restartBtn.on('pointerup', () => this.restart());
    quitBtn.on('pointerup', () => this.quit());

    void overlay;
  }

  private getGameplayScene(): GameplayScene {
    return this.scene.get(this.gameplaySceneKey) as GameplayScene;
  }

  private resume(): void {
    this.getGameplayScene().resumeFromPause();
    this.scene.stop();
  }

  private restart(): void {
    const songId = this.getGameplayScene().getSongId();
    this.scene.stop(this.gameplaySceneKey);
    this.scene.stop();
    this.scene.start(this.gameplaySceneKey, { songId });
  }

  private quit(): void {
    this.scene.stop(this.gameplaySceneKey);
    this.scene.stop();
    this.scene.start('HomeScene');
  }
}
